import type {
  DesiredContentSize,
  ScreenRect,
} from "@/lib/canvas-presentation-types";

function selectorValue(value: string): string {
  return typeof CSS !== "undefined" && CSS.escape
    ? CSS.escape(value)
    : value.replace(/["\\]/g, "\\$&");
}

/**
 * The frame's mounted body: its focus window first, else its canvas node. A
 * wall Pinned tile (`data-wall-tile`) is a capped stand-in, never the body.
 */
export function findMountedFrameElement(frameId: string): Element | null {
  const frame = `[data-canvas-frame-id="${selectorValue(frameId)}"]`;
  return (
    document.querySelector(`[data-focus-window] ${frame}`) ??
    document.querySelector(`${frame}:not([data-wall-tile] *)`)
  );
}

function primaryMediaAspectRatio(
  root: Element,
  frameType: string | undefined,
): number | undefined {
  if (frameType === "image") {
    const image = root.querySelector("img") as HTMLImageElement | null;
    if (image?.naturalWidth && image.naturalHeight) {
      return image.naturalWidth / image.naturalHeight;
    }
  }
  if (frameType === "html") {
    const video = root.querySelector("video") as HTMLVideoElement | null;
    if (video?.videoWidth && video.videoHeight) {
      return video.videoWidth / video.videoHeight;
    }
  }
  return undefined;
}

/** HTML frames report their document size over the frame message bridge. */
function iframeContentSize(
  root: Element,
): { width: number; height: number } | null {
  const { contentWidth, contentHeight } =
    root.querySelector("iframe")?.dataset ?? {};
  if (!contentWidth || !contentHeight) return null;
  return { width: Number(contentWidth), height: Number(contentHeight) };
}

/** The element whose scroll size is a frame body's content size. */
export function frameMeasureContent(root: Element): HTMLElement | null {
  return root.querySelector<HTMLElement>(
    "[data-frame-measure-content], [data-frame-content]",
  );
}

/**
 * Whether a mounted frame body reports what its content fit needs: an
 * image's pixels, an HTML document's size (or its video's), else content
 * that laid out to a size.
 */
export function frameContentReported(
  root: Element,
  frameType: string | undefined,
): boolean {
  if (primaryMediaAspectRatio(root, frameType)) return true;
  if (frameType === "image") return false;
  if (frameType === "html") return iframeContentSize(root) !== null;
  const content = frameMeasureContent(root);
  return !!content && content.scrollWidth > 0 && content.scrollHeight > 0;
}

/**
 * Width over height of the box that shows all of a mounted body whose type
 * scales with its width (an object frame): the ratio holds at any size.
 * Undefined until the body is mounted and laid out.
 */
export function measureScaledContentAspect(
  frameId: string,
): number | undefined {
  const root = findMountedFrameElement(frameId);
  const content = root ? frameMeasureContent(root) : null;
  if (!root || !content) return undefined;
  const chromeHeight = Math.max(0, root.clientHeight - content.clientHeight);
  const height = Math.max(
    root.clientHeight,
    content.scrollHeight + chromeHeight,
  );
  return root.clientWidth > 0 && height > 0
    ? root.clientWidth / height
    : undefined;
}

/**
 * Measures one frame's mounted body without owning any layout policy;
 * `fallback` is its size when nothing is mounted.
 */
export function measureFramePresentationContent(
  frameId: string,
  fallback: { width: number; height: number },
  frameType: string | undefined,
  desired: DesiredContentSize | undefined,
): {
  width: number;
  height: number;
  aspectRatio?: number;
  mounted: boolean;
} {
  const root = findMountedFrameElement(frameId);
  const content = root ? frameMeasureContent(root) : null;
  const iframeSize = root ? iframeContentSize(root) : null;
  const chromeWidth =
    root && content ? Math.max(0, root.clientWidth - content.clientWidth) : 0;
  const chromeHeight =
    root && content ? Math.max(0, root.clientHeight - content.clientHeight) : 0;
  const measuredWidth = Math.max(
    fallback.width,
    root?.scrollWidth ?? 0,
    (content?.scrollWidth ?? 0) + chromeWidth,
    (iframeSize?.width ?? 0) + chromeWidth,
  );
  const measuredHeight = Math.max(
    fallback.height,
    root?.scrollHeight ?? 0,
    (content?.scrollHeight ?? 0) + chromeHeight,
    (iframeSize?.height ?? 0) + chromeHeight,
  );
  const aspectRatio =
    desired?.aspectRatio ??
    (root ? primaryMediaAspectRatio(root, frameType) : undefined);
  const width =
    desired?.width ??
    (desired?.height && aspectRatio
      ? desired.height * aspectRatio
      : measuredWidth);
  const height =
    desired?.height ??
    (desired?.width && aspectRatio
      ? desired.width / aspectRatio
      : measuredHeight);
  return {
    width,
    height,
    ...(aspectRatio ? { aspectRatio } : {}),
    mounted: Boolean(root),
  };
}

/** Returns the currently painted explanation bubble in window coordinates. */
export function measureAgentCursorBubble(
  agentId?: string | null,
): ScreenRect | null {
  const specific = agentId
    ? document.querySelector<HTMLElement>(
        `[data-testid="agent-cursor-${selectorValue(agentId)}"]`,
      )
    : null;
  const element =
    specific ??
    document.querySelector<HTMLElement>('[data-testid="desktop-agent-cursor"]');
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;
  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
  };
}
